<template>
  <public-page>
    <v-row align="center" justify="center" class="fill-height">
      <v-col cols="12" lg="6" md="8" sm="12">
        <v-card class="auth-card" elevation="8" rounded="lg" dark>
          <v-toolbar class="auth-toolbar" flat dark height="140">
            <v-img
              :src="require('@/assets/camceadhub-logo-dark.png')"
              contain
              max-height="116"
              max-width="340"
              class="mx-auto"
            ></v-img>
          </v-toolbar>

          <v-carousel
            :hide-delimiters="true"
            :show-arrows="false"
            height="auto"
            v-model="carousel"
          >
            <v-carousel-item>
              <v-card-text>
                <v-alert
                  v-if="expiredNote"
                  type="info"
                  text
                  dense
                  class="mb-4"
                >
                  Your previous sign-in had expired, so it was removed from this
                  browser. Please sign in again.
                </v-alert>
                <p>Sign in with your username and password:</p>
                <v-alert
                  v-if="error"
                  type="error"
                  border="right"
                  colored-border
                  elevation="2"
                >
                  {{ error }}
                  <v-btn
                    v-if="needs_verify"
                    class="ml-2"
                    color="error"
                    dark
                    @click="$router.push({ name: 'ActivationEmail' }, () => {})"
                  >
                    Resend verification email
                  </v-btn>
                </v-alert>
                <v-form
                  lazy-validation
                  v-model="valid"
                  id="form"
                  ref="form"
                  action="POST"
                  @submit.prevent="userSignIn"
                >
                  <v-container class="text-center">
                    <v-text-field
                      label="Username"
                      :min="0"
                      :counter="25"
                      v-model="signInDetails.username"
                      :rules="nameRules"
                    ></v-text-field>
                    <v-text-field
                      label="Password"
                      hint="At least 8 characters"
                      :append-icon="togglepass ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="togglepass ? 'text' : 'password'"
                      :min="0"
                      :counter="25"
                      v-model="signInDetails.password"
                      :rules="passRules"
                      @click:append="togglepass = !togglepass"
                    ></v-text-field>
                  </v-container>
                </v-form>

                <v-divider class="my-4"></v-divider>
                <p class="text-center mb-2">Or sign in as a CAMCEAD member:</p>
                <v-alert
                  v-if="ravenError"
                  type="error"
                  border="right"
                  colored-border
                  elevation="2"
                >
                  {{ ravenError }}
                  <div v-if="ravenRestart" class="mt-2">
                    <v-btn
                      small
                      outlined
                      color="error"
                      :loading="loading_raven"
                      @click="signInWithRaven"
                    >
                      <v-icon left small>mdi-restart</v-icon>
                      Sign in with CRSid again
                    </v-btn>
                  </div>
                </v-alert>
                <div class="text-center">
                  <v-btn
                    outlined
                    color="grey lighten-1"
                    :loading="loading_raven"
                    @click="signInWithRaven"
                  >
                    <v-icon left>mdi-shield-account</v-icon>
                    CRSid (CAMCEAD members only)
                  </v-btn>
                </div>
              </v-card-text>
              <v-divider class="auth-divider"></v-divider>
              <v-card-actions class="flex-wrap">
                <v-btn
                  color="grey lighten-1"
                  plain
                  @click="
                    $router.push({ name: 'PasswordResetEmail' }, () => {})
                  "
                >
                  Forgot password?
                </v-btn>
                <v-btn
                  color="grey lighten-1"
                  plain
                  @click="$router.push({ name: 'SignUp' }, () => {})"
                >
                  Not a member yet? Register
                </v-btn>

                <v-spacer></v-spacer>
                <v-btn
                  type="submit"
                  form="form"
                  outlined
                  color="grey lighten-1"
                  :disabled="!valid"
                  @click="validate"
                >
                  <v-icon left>mdi-lock</v-icon>
                  Login
                </v-btn>
              </v-card-actions>
            </v-carousel-item>
            <v-carousel-item>
              <v-col cols="12" class="text-center pa-16">
                <v-progress-circular
                  :size="70"
                  :width="2"
                  color="grey lighten-1"
                  class="pa-16"
                  indeterminate
                  >Logging in...</v-progress-circular
                >
              </v-col>
            </v-carousel-item>
            <v-carousel-item>
              <v-card-text>
                <p>
                  If you have mobile authentication set up, please generate a
                  code in your authenticator app and enter it below.
                  Otherwise, click "Email me a one-time code" and we'll send
                  one to your registered email address.
                </p>
                <v-alert
                  v-if="error && carousel === 2"
                  type="error"
                  border="right"
                  colored-border
                  elevation="2"
                >
                  {{ error }}
                </v-alert>
                <v-form
                  lazy-validation
                  v-model="validOTP"
                  action="POST"
                  @submit.prevent="userValidateOTP"
                  id="formOTP"
                  ref="formOTP"
                >
                  <v-container class="text-center">
                    <v-text-field
                      label="Authentication Code"
                      :min="0"
                      :counter="25"
                      v-model="signInDetails.token"
                      @keyup="codeCheck"
                    ></v-text-field>
                  </v-container>
                </v-form>
              </v-card-text>
              <v-divider class="auth-divider"></v-divider>
              <v-card-actions>
                <v-btn v-if="emailCode" outlined color="grey lighten-1" @click="setupOTP"
                  >Email me a one-time code</v-btn
                >
                <v-spacer></v-spacer>
                <v-btn
                  outlined
                  color="grey lighten-1"
                  type="submit"
                  form="formOTP"
                  @click="validateOTP"
                >
                  <v-icon left>mdi-lock</v-icon>
                  Verify
                </v-btn>
              </v-card-actions>
            </v-carousel-item>
          </v-carousel>
        </v-card>
      </v-col>
    </v-row>
  </public-page>
</template>

<script>
import {
  ACCOUNTS_AZUREAD_AUTHORIZE,
  ACCOUNTS_TOKEN,
  API,
  EMAIL_SETUP_OTP,
  VALIDATE_OTP,
} from "@/axios-config";
import { isWellFormedToken, ravenErrorInfo } from "@/utils/authSession";
import PublicPage from "@/components/PublicPage";
import { returnToCasperConsent } from "@/casperConsent";

import { useUserStore } from "@/store/user";
import { attemptAdminToolRedirect } from "@/utils/adminTools";

export default {
  name: "SignInView",
  setup() {
    const userStore = useUserStore();
    return { userStore };
  },
  components: {
    PublicPage,
  },
  data() {
    return {
      signInDetails: {
        username: "",
        password: "",
        token: "",
        uuid: "",
      },
      nameRules: [
        (v) => !!v || "Username is required",
        (v) =>
          (v && v.length > 4) ||
          "A username must be more than 5 characters long",
        (v) =>
          /^[A-Za-z0-9_\\.]+$/.test(v) ||
          "A username can only contain letters and digits",
      ],
      passRules: [
        (v) => !!v || "Password is required",
        (v) =>
          (v && v.length > 7) ||
          "The password must be longer than 7 characters",
      ],
      togglepass: false,
      valid: false,
      validOTP: false,
      error: null,
      needs_verify: false,
      carousel: 0,
      enforceOTP: process.env.VUE_APP_ENFORCE_OTP === "true",
      emailCode: true,
      loading_raven: false,
      ravenError: "",
      ravenRestart: false,
      expiredNote: false,
    };
  },
  created() {
    const code = this.$route.query.raven_error;
    if (code) {
      const info = ravenErrorInfo(code);
      this.ravenError = info.text;
      this.ravenRestart = info.restart;
    }
    this.expiredNote = !!this.$route.query.expired;
    // The error code and the note belong to the visit that produced them: take them out of the address so that a reload, a
    // bookmark or the Back button does not show them again.
    if (code || this.$route.query.expired) {
      const query = { ...this.$route.query };
      delete query.raven_error;
      delete query.expired;
      this.$router.replace({ name: "SignIn", query }).catch(() => {});
    }
  },
  methods: {
    async signInWithRaven() {
      this.loading_raven = true;
      this.ravenError = "";
      this.ravenRestart = false;
      this.error = null;
      // Start from nothing: whatever an earlier attempt, an old session or another tab left in this browser must not take
      // part in this sign-in.
      this.userStore.clearSession();
      try {
        const { data } = await API.get(ACCOUNTS_AZUREAD_AUTHORIZE);
        if (!data || typeof data.url !== "string") {
          throw new Error("no sign-in address in the answer");
        }
        window.location.href = data.url;
      } catch (error) {
        this.loading_raven = false;
        const status = error.response?.status;
        this.ravenError =
          status === 503
            ? ravenErrorInfo("not_configured").text
            : !error.response
            ? "Could not reach the server to start the CRSid sign-in. Check your connection and try again."
            : "Could not start the CRSid sign-in. Please try again in a minute, or use your username and password.";
        this.ravenRestart = status !== 503;
      }
    },
    userSignIn() {
      this.carousel = 1;
      this.error = null;
      this.needs_verify = false;
      this.ravenError = "";
      this.ravenRestart = false;
      this.expiredNote = false;
      // Start from nothing (see signInWithRaven).
      this.userStore.clearSession();
      API.post(ACCOUNTS_TOKEN, {
        username: this.signInDetails.username,
        password: this.signInDetails.password,
      })
        .then((response) => {
          if (!isWellFormedToken(response.data?.access) || !response.data?.user?.uuid) {
            throw new Error("the answer does not contain a sign-in");
          }
          this.userStore.setToken(response.data.access);
          this.userStore.setRefreshToken(response.data.refresh);
          this.userStore.setUser(response.data.user);

          //if (this.enforceOTP) {
          this.signInDetails.uuid = response.data.user.uuid;
          this.carousel = 2;
          //} else {
          //  this.$router.push({ name: "Profile" });
          //}
        })
        .catch((error) => {
          this.carousel = 0;
          if (!error.response) {
            this.error =
              "There is a problem connecting to the server. Please try again later.";
          } else if (error.response.status === 401) {
            if (error.response.data?.code === "not_verified") {
              this.needs_verify = true;
              this.error = "Please verify your email address";
            } else if (error.response.data?.code === "not_approved") {
              this.error =
                "User has not been approved yet. Please try again later.";
            } else {
              this.error = "Invalid username or password";
            }
          } else {
            this.error =
              "There is a problem connecting to the server. Please try again later.";
          }
        });
    },
    userValidateOTP() {
      API.post(VALIDATE_OTP, {
        uuid: this.userStore.uuid,
        token: this.signInDetails.token,
      })
        .then((response) => {
          if (!isWellFormedToken(response.data?.access) || !response.data?.user) {
            // The server did not hand out a sign-in (it used to answer 200 {"status": "error"}, and "undefined" was stored as
            // the token). Never store that.
            throw new Error("the answer does not contain a sign-in");
          }
          this.userStore.setToken(response.data.access);
          this.userStore.setRefreshToken(response.data.refresh);
          this.userStore.setUser(response.data.user);
          this.userStore.setOTP(true);

          // Redirect target for external services gated by nginx's auth_request
          // (Headlamp, Grafana, Prometheus - all 3 sites) — a real browser navigation
          // outside the SPA's own router, so it needs a Django session minted first
          // (the JWT we just got isn't visible to a plain page load the way it is to
          // the SPA's own axios calls). ADMIN_TOOL_REDIRECTS (adminTools.js) is the one
          // place every real `next` value nginx can send is listed - added 2026-09-22
          // after this used to hardcode only the original 2 ARCUS paths and silently
          // dropped every other site's `next` to a bare Profile redirect. (Projects/
          // camcead-astro isn't part of this - it has its own standalone login,
          // unrelated to CamCeadHub's own sign-in.)
          if (returnToCasperConsent()) {
            return;
          }
          const next = this.$route.query.next;
          const handled = attemptAdminToolRedirect(next, (error) => {
            console.log(error);
            this.error =
              "Signed in, but could not start that session. If you're not on the " +
              "Cambridge network or VPN, that may be why - some admin tools are " +
              "restricted to it.";
            this.carousel = 0;
          });
          if (handled) {
            return;
          }

          this.$router.push({ name: "Profile" });
        })
        .catch((error) => {
          const status = error.response?.status;
          if (status === 400 && error.response?.data?.status === "fail") {
            // wrong or expired one-time code: stay on this step and let the person type another one
            this.error = "That code was not accepted. Check it and try again (an e-mailed code lasts about 10 minutes).";
            this.carousel = 2;
          } else if (!error.response) {
            this.error = "There is a problem connecting to the server. Please try again later.";
            this.carousel = 2;
          } else {
            this.error = "This sign-in could not be completed. Please sign in again.";
            this.carousel = 0;
          }
        });
    },
    validate() {
      this.error = null;
      this.$refs.form.validate();
    },
    validateOTP() {
      this.error = null;
      this.$refs.formOTP.validate();
    },
    setupOTP() {
      API.post(EMAIL_SETUP_OTP, {
        uuid: this.userStore.uuid,
      })
        .then(() => {
          this.signInDetails.token = "";
          this.emailCode = false;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    codeCheck() {
      return;
      //if (this.signInDetails.token === "Send me an email code") {
      //  this.emailCode = true;
      //} else {
      //  this.emailCode = false;
      //}
    },
  },
  computed: {
    maxHeight() {
      const height = this.$vuetify.breakpoint.mdAndUp ? "100vh" : "50vh";
      return `calc(${height} + 0px - ${this.$vuetify.application.top}px)`; // 66px is the height of the app bar
    },
    imgSrc() {
      return require("@/assets/bg3.2.jpg");
    },
  },
};
</script>

<style scoped>
.v-btn {
  border-radius: 4px;
}

.auth-card.v-card {
  background-color: #101014 !important;
}

.auth-toolbar.v-toolbar {
  background-color: #101014 !important;
}

.auth-divider.v-divider {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.auth-card ::v-deep input:-webkit-autofill,
.auth-card ::v-deep input:-webkit-autofill:hover,
.auth-card ::v-deep input:-webkit-autofill:focus,
.auth-card ::v-deep input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px #101014 inset !important;
  -webkit-text-fill-color: #fff !important;
  caret-color: #fff !important;
  transition: background-color 9999s ease-in-out 0s;
}
</style>
